import React, { useEffect, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import {
  evidenceAnalysisJobRef,
  runSchemaV2EvidenceWorkersUntilDone,
} from '../services/aiPipelines/schemaV2';

const STATUS_STYLE = {
  queued: { bg: 'rgba(148,163,184,0.2)', color: '#cbd5e1', label: 'Queued' },
  analysing: { bg: 'rgba(251,191,36,0.2)', color: '#fbbf24', label: 'Processing' },
  analyzing: { bg: 'rgba(251,191,36,0.2)', color: '#fbbf24', label: 'Processing' },
  extracted: { bg: 'rgba(52,211,153,0.2)', color: '#34d399', label: 'Done' },
  done: { bg: 'rgba(52,211,153,0.2)', color: '#34d399', label: 'Done' },
  skipped_photo_policy: { bg: 'rgba(248,113,113,0.15)', color: '#fca5a5', label: 'Photo skipped' },
  skipped_video: { bg: 'rgba(167,139,250,0.18)', color: '#c4b5fd', label: 'Video skipped' },
  skipped_duplicate: { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8', label: 'Duplicate' },
  failed: { bg: 'rgba(248,113,113,0.25)', color: '#f87171', label: 'Failed' },
};

/**
 * Live per-file evidence analysis board (schema_v2).
 * Hide closes the viewer only — the Analyse/Resume chain keeps running in the parent session.
 */
const EvidenceAnalysisProgressModal = ({ schoolId, schoolName, open, onHide }) => {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(null);
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    if (!open || !schoolId) return undefined;
    const unsub = onSnapshot(evidenceAnalysisJobRef(schoolId), (snap) => {
      setJob(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [open, schoolId]);

  if (!open) return null;

  const files = Array.isArray(job?.files) ? job.files : [];
  const total = job?.total ?? files.length;
  const failedFiles = files.filter((f) => f.status === 'failed');
  const done =
    job?.done ??
    files.filter((f) =>
      ['extracted', 'done', 'skipped_photo_policy', 'skipped_video', 'skipped_duplicate', 'failed'].includes(f.status)
    ).length;
  const queued = files.filter((f) => f.status === 'queued').length;
  const analysing = files.filter((f) => f.status === 'analysing' || f.status === 'analyzing').length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isComplete =
    job?.status === 'complete' || (total > 0 && queued === 0 && analysing === 0);
  const canResume =
    !isComplete &&
    (queued > 0 || analysing > 0 || job?.phase === 'v2b_paused_budget' || job?.workerStatus === 'idle');

  const runChain = async (key, opts) => {
    if (!schoolId || busy) return;
    setBusy(key);
    setActionError(null);
    try {
      await runSchemaV2EvidenceWorkersUntilDone(schoolId, opts);
    } catch (err) {
      setActionError(err?.message || 'Could not continue analysis.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Evidence analysis progress"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        background: 'rgba(2, 6, 23, 0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onHide}
    >
      <div
        style={{
          width: 'min(720px, 100%)',
          maxHeight: '85vh',
          overflow: 'auto',
          background: '#0f172a',
          border: '1px solid rgba(19, 138, 138, 0.4)',
          borderRadius: 12,
          padding: 20,
          boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: '1.05rem' }}>Evidence analysis</h3>
            <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: '0.85rem' }}>
              {schoolName || schoolId} · Schema v2 · one file per server step (auto-continues)
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {failedFiles.length > 0 ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => runChain('retry', { force: true, retryFailed: true })}
                disabled={!!busy}
                style={{ height: 36 }}
                title="Put failed files back in the queue and continue"
              >
                {busy === 'retry' ? 'Retrying…' : `Retry failed (${failedFiles.length})`}
              </button>
            ) : null}
            {canResume ? (
              <button
                type="button"
                className="btn-primary-action"
                onClick={() => runChain('resume', { force: true })}
                disabled={!!busy}
                style={{ height: 36 }}
                title="Continue queued files until finished"
              >
                {busy === 'resume' ? 'Resuming…' : 'Resume'}
              </button>
            ) : null}
            <button type="button" className="btn-secondary" onClick={onHide} style={{ height: 36 }}>
              {isComplete ? 'Close' : 'Hide (keep running)'}
            </button>
          </div>
        </div>

        {actionError ? (
          <p style={{ color: '#fca5a5', fontSize: '0.8rem', marginTop: 0 }}>{actionError}</p>
        ) : null}

        {failedFiles.length > 0 ? (
          <p style={{ color: '#fcd34d', fontSize: '0.78rem', marginTop: 0, lineHeight: 1.4 }}>
            {failedFiles.length} file(s) failed (often oversized PDF or model JSON). Use{' '}
            <strong>Retry failed</strong> to re-queue them, or open the file yourself and rely on SEF/schema for that item.
          </p>
        ) : null}

        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cbd5e1', fontSize: '0.85rem' }}>
            <span>
              {done} of {total} documents handled
              {analysing > 0 ? ` · ${analysing} processing` : ''}
              {queued > 0 ? ` · ${queued} queued` : ''}
              {failedFiles.length > 0 ? ` · ${failedFiles.length} failed` : ''}
            </span>
            <span>{pct}%</span>
          </div>
          <div
            style={{
              marginTop: 8,
              height: 8,
              borderRadius: 4,
              background: 'rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                height: '100%',
                background: '#138a8a',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          {job?.note ? (
            <p style={{ margin: '10px 0 0', color: '#64748b', fontSize: '0.78rem', lineHeight: 1.4 }}>
              {job.note}
            </p>
          ) : null}
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          {files.length === 0 ? (
            <p style={{ color: '#94a3b8', margin: 0 }}>No evidence files in the vault for this school.</p>
          ) : (
            files.map((f, idx) => {
              const st = STATUS_STYLE[f.status] || STATUS_STYLE.queued;
              return (
                <div
                  key={`${f.name}-${idx}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '28px 1fr auto',
                    gap: 10,
                    alignItems: 'start',
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <span style={{ color: '#64748b', fontSize: '0.8rem' }}>{idx + 1}.</span>
                  <div>
                    <div style={{ color: '#e2e8f0', fontSize: '0.88rem', wordBreak: 'break-word' }}>
                      {f.name}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: 2 }}>
                      {f.kind || 'file'}
                      {f.detail ? ` — ${f.detail}` : ''}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      padding: '4px 8px',
                      borderRadius: 4,
                      background: st.bg,
                      color: st.color,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {st.label}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default EvidenceAnalysisProgressModal;